import {atom} from 'jotai';
import type {DocumentNode} from '@apollo/client';
import {getAllQueuedMutations, putQueuedMutation, deleteQueuedMutation} from '@/lib/offlineDb';
import {generateUUID} from '@/lib/util.ts';

/**
 * Generic queue of pending writes made while offline (or while a
 * write fails for any reason). Each entry captures enough to replay
 * the mutation later: which GraphQL mutation, what variables, plus
 * bookkeeping so the UI can show "queued" state on the record that
 * was edited.
 *
 * Persisted in IndexedDB (see lib/offlineDb.ts), not localStorage —
 * no realistic size ceiling and writes don't block the UI thread,
 * which matters if a field user queues dozens of edits over days
 * with no signal.
 */
export interface QueuedMutation {
   id: string; // uuid, generated client-side
   /** Human label for UI, e.g. "Update Hartley Fed #3H daily report" */
   label: string;
   /** Which mutation this replays — kept as a string key resolved to a
    *  DocumentNode via MUTATION_REGISTRY, since DocumentNode itself
    *  doesn't survive serialization to IndexedDB. */
   mutationKey: keyof typeof MUTATION_REGISTRY;
   variables: Record<string, unknown>;
   /** Entity this mutation targets, e.g. `well:123` — also the
    *  IndexedDB key, so a second queued edit to the same record
    *  overwrites the first rather than stacking duplicates. */
   entityKey: string;
   createdAt: string;
   /** Bumped on each failed retry; surfaced in the sync UI. */
   retryCount: number;
   lastError?: string;
}

// Populated by graphql/*.ts modules at import time via registerMutation,
// so this file doesn't need to import every mutation document directly
// (avoids a circular-import edge case with the sync hook).
export const MUTATION_REGISTRY: Record<string, DocumentNode> = {};

export function registerMutation(key: string, doc: DocumentNode) {
   MUTATION_REGISTRY[key] = doc;
}

/**
 * In-memory mirror of what's in IndexedDB, for synchronous reads in
 * components (e.g. "is this row queued?"). Hydrated on app start via
 * hydrateOfflineQueueAtom and kept in sync by every write below —
 * components should never need to read IndexedDB directly.
 */
export const offlineQueueAtom = atom<QueuedMutation[]>([]);
export const offlineQueueHydratedAtom = atom(false);

export const hydrateOfflineQueueAtom = atom(null, async (_get, set) => {
   const entries = await getAllQueuedMutations();
   set(offlineQueueAtom, entries);
   set(offlineQueueHydratedAtom, true);
});

export type SyncState = 'idle' | 'syncing' | 'error';
export const syncStateAtom = atom<SyncState>('idle');

/** Add or replace a queued mutation for a given entity (latest edit wins). */
export const enqueueMutationAtom = atom(
   null,
   async (get, set, entry: Omit<QueuedMutation, 'id' | 'createdAt' | 'retryCount'>) => {
      const full: QueuedMutation = {
         ...entry,
         id: generateUUID(),
         createdAt: new Date().toISOString(),
         retryCount: 0,
      };
      await putQueuedMutation(full); // keyPath is entityKey, so this overwrites any prior queued edit to the same record
      const withoutSameEntity = get(offlineQueueAtom).filter((q) => q.entityKey !== entry.entityKey);
      set(offlineQueueAtom, [...withoutSameEntity, full]);
   },
);

export const removeFromQueueAtom = atom(null, async (get, set, id: string) => {
   const queue = get(offlineQueueAtom);
   const target = queue.find((q) => q.id === id);
   if (target) await deleteQueuedMutation(target.entityKey);
   set(
      offlineQueueAtom,
      queue.filter((q) => q.id !== id),
   );
});

/**
 * Reconciles the in-memory queue and IndexedDB after a sync pass:
 * entries that succeeded are deleted, entries that failed again are
 * upserted with their bumped retryCount/lastError. Called by
 * useOfflineSync — not meant to be called from UI code.
 *
 * REMOVED 2026-08-20: this used to also call deletePendingPhoto for
 * every succeededEntityKeys entry, on the theory that once the ticket
 * record's own mutation synced, its photo was no longer needed locally.
 * That was wrong — the ticket record and its photo are two independent
 * things to sync (see lib/offlineDb.ts's PHOTO_STORE_NAME comment), and
 * this deleted the local blob before it had ever actually been uploaded
 * to S3, since that upload step didn't exist yet at the time. A pending
 * photo is now only ever deleted by state/runTicketPhotoSync.ts, once
 * IT has actually synced — never as a side effect of this function.
 */
export const applySyncResultAtom = atom(
   null,
   async (_get, set, result: {succeededEntityKeys: string[]; failures: QueuedMutation[]}) => {
      await Promise.all([
         ...result.succeededEntityKeys.map((key) => deleteQueuedMutation(key)),
         ...result.failures.map((entry) => putQueuedMutation(entry)),
      ]);
      set(offlineQueueAtom, result.failures);
   },
);
